import {
  AfterViewInit,
  Component,
  DestroyRef,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TinyHelpers } from '@coin/shared/core/util-helpers';
import { AuthService } from '@coin/shared/auth/data-access';
import { CmsContentItemContent, CmsItem, TransformEmailHtmlHelper } from '@coin/shared/cms/util';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTabGroup, MatTab, MatTabLabel, MatTabContent } from '@angular/material/tabs';
import { NgStyle, NgClass } from '@angular/common';
import { BasicEmailComponent } from '../basic-email/basic-email.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-email-preview',
  templateUrl: './email-preview.component.html',
  styleUrls: ['./email-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatTabGroup, MatTab, MatTabLabel, MatTabContent, NgStyle, BasicEmailComponent, NgClass]
})
export class EmailPreviewComponent implements OnInit, AfterViewInit, DoCheck {
  authService = inject(AuthService);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('mainSize', { static: false }) mainSize: ElementRef<HTMLDivElement>;
  @ViewChildren('emailPreviewRoot', { read: ElementRef }) emailPreviewQueryList: QueryList<HTMLDivElement>;

  @Output() previewHeadlineClicked = new EventEmitter<void>();
  @Output() previewItemClicked = new EventEmitter<CmsItem>();
  @Input() item: CmsContentItemContent;
  @Input() maxHeight = 380;
  @Input() heightModified = false;
  @Input() frame = false;

  public selectedTab = 0;
  public zoomValues: Record<string, number> = {};
  public emailContentHtml: SafeHtml;

  private emailPreviewRootElement: ElementRef<HTMLDivElement>;
  private initialItem: CmsContentItemContent;
  private readonly updateEmailContent$ = new Subject<void>();
  private addedClickHandlers = false;

  constructor() {
    // fromEvent to window resize trigger content update
    fromEvent(window, 'resize')
      .pipe(debounceTime(200))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateEmailContent$.next();
        this.setupViews();
        this.changeOfTab(this.selectedTab);
      });
  }

  public ngOnInit(): void {
    this.listenToEmailContentUpdate();
    this.setupViews();
  }

  public ngAfterViewInit(): void {
    this.emailPreviewQueryList.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => {
      if (list.first && !this.addedClickHandlers) {
        this.emailPreviewRootElement = list.first;
        this.addClickHandlerToPreviewItems();
        this.addedClickHandlers = true;
      }
    });
  }

  public ngDoCheck(): void {
    const contentChanged = !TinyHelpers.areItemsSimilar(this.item, this.initialItem);

    if (!contentChanged) {
      return;
    }

    this.updateInitialItem();
    this.updateEmailContent$.next();
  }

  private makePreviewElementClickable(id: string, clickHandler: (event: MouseEvent) => void): void {
    const elementInPreview = this.emailPreviewRootElement.nativeElement.shadowRoot.querySelector(
      `[id='${id}']`
    ) as HTMLDivElement;
    if (elementInPreview) {
      const initialTransform = elementInPreview.style.transform;
      const initialBoxShadow = elementInPreview.style.boxShadow;
      elementInPreview.style.cursor = 'pointer';
      elementInPreview.style.pointerEvents = 'auto';
      elementInPreview.style.transition = 'transform .5s;';
      elementInPreview.addEventListener('click', clickHandler);
      elementInPreview.addEventListener('mouseenter', () => {
        elementInPreview.style.transform = 'scale(1.02)';
        elementInPreview.style.boxShadow = '0 4px 10px rgb(0, 0, 0, 0.3)';
      });
      elementInPreview.addEventListener('mouseleave', () => {
        elementInPreview.style.transform = initialTransform;
        elementInPreview.style.boxShadow = initialBoxShadow;
      });
    }
  }

  private addClickHandlerToPreviewItems(): void {
    if (this.emailPreviewRootElement) {
      this.makePreviewElementClickable('headline-container', event => {
        event.preventDefault();
        this.previewHeadlineClicked.emit();
      });
      for (const cmsItem of this.item.content) {
        this.makePreviewElementClickable(cmsItem.id, event => {
          // Pr event clicking of anchors that might be in the preview element
          event.preventDefault();
          this.previewItemClicked.emit(cmsItem);
        });
      }
    }
  }

  private updateInitialItem(): void {
    this.initialItem = structuredClone(this.item);
  }

  private listenToEmailContentUpdate(): void {
    this.updateEmailContent$
      .pipe(debounceTime(200))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateEmailContent());
  }

  private updateEmailContent(): void {
    this.emailContentHtml = this.domSanitizer.bypassSecurityTrustHtml(
      TransformEmailHtmlHelper.setupEmailHtml('preview-email', this.item)
    );
    this.addedClickHandlers = false;
    setTimeout(() => {
      this.emailPreviewQueryList?.notifyOnChanges();
    });
  }

  private setupViews(): void {
    setTimeout(() => {
      this.changeOfTab(2);
      this.changeOfTab(1);
      this.changeOfTab(0);
    }, 200);
  }

  /**
   * When an icon for a view is clicked the zoom resolution for each view will be recalculated based on current browser resolution
   * @param index change tab index
   */
  public changeOfTab(index: number): void {
    this.selectedTab = index;
    this.addedClickHandlers = false;
    setTimeout(() => {
      switch (index) {
        case 0:
          this.getZoom('desktop', 1900);
          break;
        case 1:
          this.getZoom('mobile', 700);
          break;
        case 2:
          this.getZoom('laptop', 1150);
          break;
      }
    }, 600);
  }

  /**
   * Generate zoom resolution level based on current browser resolution
   * @param type which kind of resolution is requested
   * @param width specified width that should be used to generate zoom resolution of preview
   */
  public getZoom(type: string, width: number): void {
    let elemWidth = 0;
    if (type && this.mainSize && this.mainSize.nativeElement) {
      elemWidth = this.mainSize.nativeElement.offsetWidth;
      if (elemWidth > 0) {
        this.zoomValues[type] = elemWidth / width;
      } else {
        setTimeout(() => {
          this.getZoom(type, width);
        }, 1200);
      }
    }
  }
}
